export const PHASES = [
    {
        number: 1,
        name: "GeneratePitInputs",
        description: "Generates terrain and per-point PIT sheltering coefficients from Root GeoJSON.",
        datasetType: "root-geojson",
        datasetLabel: "Root GeoJSON Version",
    },
    {
        number: 2,
        name: "AggregatePitCoefficients",
        description: "Aggregates per-point coefficients into per-line and per-section coefficients.",
        datasetType: "pit-prediction-manifest",
        datasetLabel: "PIT Prediction Manifest Version",
    },
    {
        number: 3,
        name: "ComputeSectionHistoricalDlr",
        description: "Computes historical DLR using TWC weather data and section PIT coefficients.",
        datasetType: "section-pit-coefficients",
        datasetLabel: "Section PIT Coefficients Version",
    },
    {
        number: 4,
        name: "BuildLineHistoricalDlr",
        description:
            "Partitions section historical DLR into per-line outputs and emits the line-level historical DLR dataset.",
        datasetType: "section-historical-dlr",
        datasetLabel: "Section Historical DLR Version",
    },
];

export const ALL_PHASE_NUMBERS = PHASES.map((p) => p.number);

// Derives Phase-3 date state from raw <input type="date"> string values.
// Dates that are today or in the future are treated as unset so the backend
// applies its own defaults (start = 5 years ago, end = today).
export function computePhase3DateRange(startDate, endDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDateObj = startDate ? new Date(startDate) : null;
    const endDateObj = endDate ? new Date(endDate) : null;
    const startDateIsFuture = startDateObj && startDateObj >= today;
    const endDateIsFuture = endDateObj && endDateObj >= today;
    const effectiveStart = startDate && !startDateIsFuture ? startDateObj : null;
    const effectiveEnd = endDate && !endDateIsFuture ? endDateObj : null;
    const rangeEnd = effectiveEnd || today;
    const dateRangeError =
        effectiveStart && effectiveEnd && effectiveStart > effectiveEnd
            ? "Start date must be before end date."
            : effectiveStart && rangeEnd - effectiveStart > 5 * 365.25 * 24 * 60 * 60 * 1000
            ? "Date range exceeds 5 years — please narrow the window."
            : null;
    return { effectiveStart, effectiveEnd, startDateIsFuture, endDateIsFuture, dateRangeError };
}

// Builds the human-readable confirmation lines describing the optional Phase 1
// resume flag (which disables the backend's default overwrite) and the Phase 3
// date range / resume flag. Shared by both kickoff pages so the wording (and the
// "default" fallbacks) live in one place.
export function buildPhaseExtraSummary({
    selectedPhases,
    phase1Resume,
    phase3Resume,
    startDate,
    endDate,
    effectiveStart,
    effectiveEnd,
}) {
    const lines = [];
    if (selectedPhases.includes(1) && phase1Resume) {
        lines.push("Phase 1 will resume (overwrite disabled).");
    }
    if (selectedPhases.includes(3)) {
        lines.push(`Start date: ${effectiveStart ? startDate : "default (5 years ago)"}`);
        lines.push(`End date: ${effectiveEnd ? endDate : "default (today)"}`);
        if (phase3Resume) lines.push("Resume from previous incomplete run.");
    }
    return lines;
}

// Toggle a phase number in a consecutive selection. The result is always a
// contiguous range (or empty).
//
// Checking extends the range to include phaseNumber, filling any gap whether
// the new phase sits above the current max or below the current min.
// Unchecking removes that phase and every phase downstream of it — same as the
// original cascade.
export function toggleConsecutivePhase(prev, phaseNumber) {
    if (prev.includes(phaseNumber)) {
        return prev.filter((p) => p < phaseNumber);
    }
    if (prev.length === 0) {
        return [phaseNumber];
    }
    const newMin = Math.min(Math.min(...prev), phaseNumber);
    const newMax = Math.max(Math.max(...prev), phaseNumber);
    const filled = [];
    for (let p = newMin; p <= newMax; p++) filled.push(p);
    return filled;
}
